import React, { useEffect, useState } from 'react';
import '../App.css';
import {
  Globe, Share2, MessageSquare, Bot, Car,
  ChevronDown, Check, Calendar, Users,
  BarChart3, Tag, Package, Menu, X, Mail, MapPin,
  Phone, Zap, Shield, TrendingUp, Clock, Settings,
  Database, Smartphone, ShoppingBag,
  MessageCircle, FileText, Camera, Calculator
} from 'lucide-react';

/* ─── Assets ─────────────────────────────────────────── */
const LOGO_HORIZ_WHITE = 'https://customer-assets.emergentagent.com/job_canepas-platform/artifacts/qmhx033y_Logo_Artificialmente_H_Blanco.png';
const LOGO_CIRCLE = 'https://customer-assets.emergentagent.com/job_canepas-platform/artifacts/vyu6ccfj_iln3rcv4_Logo_Artificialmente_Circulo.png';
const PASS = '123321';

/* ─── Scroll Reveal ───────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.07, rootMargin: '0px 0px -30px 0px' }
    );
    document.querySelectorAll('.fade-up').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ─── Access Gate ─────────────────────────────────────── */
function AccessGate({ onUnlock }) {
  const [pass, setPass] = useState('');
  const [error, setError] = useState(false);
  const handleSubmit = e => {
    e.preventDefault();
    if (pass === PASS) { localStorage.setItem('vladimir_unlocked', '1'); onUnlock(); }
    else { setError(true); setTimeout(() => setError(false), 2500); }
  };
  return (
    <div className="gate-wrap" data-testid="access-gate">
      <div className="gate-card">
        <img src={LOGO_HORIZ_WHITE} alt="Artificialmente" className="gate-logo" data-testid="gate-logo" />
        <div className="gate-divider" />
        <span className="gate-label">Documento confidencial</span>
        <div className="gate-title">PROPUESTA VLADIMIR & AEROCAR</div>
        <p className="gate-sub">Este documento es de uso exclusivo del departamento gerencial de Automotora Vladimir y Aerocar. Ingrese la clave de acceso para continuar.</p>
        <form onSubmit={handleSubmit}>
          <input data-testid="gate-input" type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="Clave de acceso" className={`gate-input ${error ? 'error' : ''}`} autoFocus />
          {error && <div className="gate-error" data-testid="gate-error">Clave incorrecta. Intente nuevamente.</div>}
          <button type="submit" className="gate-btn" data-testid="gate-submit">Acceder a la propuesta</button>
        </form>
      </div>
    </div>
  );
}

/* ─── Scroll Progress Bar ─────────────────────────────── */
function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const h = () => { const d = document.documentElement; setP((d.scrollTop / (d.scrollHeight - d.clientHeight)) * 100); };
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  return <div className="scroll-progress" style={{ width: `${p}%` }} />;
}

/* ─── Side Navigation Dots ────────────────────────────── */
const DOTS = [
  { id: 'hero',      label: 'Inicio' },
  { id: 'ecosistema',label: 'Propuesta' },
  { id: 'modulos',   label: 'Módulos' },
  { id: 'roadmap',   label: 'Roadmap' },
  { id: 'inversion', label: 'Inversión' },
];
function SideNav() {
  const [active, setActive] = useState('hero');
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); }),
      { threshold: 0.35 }
    );
    DOTS.forEach(d => { const el = document.getElementById(d.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);
  return (
    <div className="side-nav hidden xl:flex" data-testid="side-nav">
      {DOTS.map(d => (
        <div key={d.id} className={`side-dot-item ${active === d.id ? 'active' : ''}`} onClick={() => document.getElementById(d.id)?.scrollIntoView({ behavior: 'smooth' })}>
          <span className="side-dot-label">{d.label}</span>
          <span className="side-dot" />
        </div>
      ))}
    </div>
  );
}

/* ─── Navigation ──────────────────────────────────────── */
const NAV_LINKS = [
  { label: '01. La Propuesta', id: 'ecosistema' },
  { label: '02. Roadmap',      id: 'roadmap' },
  { label: '03. Inversión',    id: 'inversion' },
];
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);
  const go = id => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setOpen(false); };
  return (
    <>
      <nav data-testid="main-nav" className={`nav-outer ${scrolled ? 'scrolled' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <button onClick={() => go('hero')} data-testid="nav-logo">
            <img src={LOGO_HORIZ_WHITE} alt="Artificialmente" className="h-8 w-auto" />
          </button>
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(n => (
              <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => go(n.id)}
                className="text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-white transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.1em' }}>
                {n.label}
              </button>
            ))}
            <button data-testid="nav-cta" onClick={() => go('inversion')} className="btn-p text-xs py-2.5 px-5">Ver inversión</button>
          </div>
          <button className="md:hidden text-gray-400 hover:text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>
      <div className={`mobile-menu-fs ${open ? 'open' : ''}`} data-testid="mobile-menu">
        <div className="mobile-menu-head">
          <img src={LOGO_HORIZ_WHITE} alt="Artificialmente" className="h-8 w-auto" />
          <button className="mobile-close-btn" onClick={() => setOpen(false)} aria-label="Cerrar menú">
            <X size={22} />
          </button>
        </div>
        <nav className="mobile-menu-links">
          {NAV_LINKS.map((n, i) => (
            <button key={n.id} onClick={() => go(n.id)}
              className="mobile-menu-item"
              style={{ animationDelay: `${i * 0.06}s` }}>
              <span className="mobile-item-num">0{i + 1}</span>
              <span>{n.label.replace(/^0\d\.\s/, '')}</span>
            </button>
          ))}
        </nav>
        <div className="mobile-menu-footer">
          <button onClick={() => go('inversion')} className="btn-p w-full justify-center" style={{ fontSize: '15px', padding: '15px' }}>
            Ver propuesta de inversión
          </button>
          <p className="text-xs text-center mt-4" style={{ color: '#475569' }}>Propuesta confidencial · Vladimir & Aerocar 2025</p>
        </div>
      </div>
      {open && <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setOpen(false)} />}
    </>
  );
}

/* ─── Hero / Portada ──────────────────────────────────── */
function Hero() {
  const go = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <section id="hero" className="hero-wrap px-6 sm:px-10" style={{ flexDirection: 'column' }}>
      <div className="hero-bg-img" />
      <div className="hero-fade-bottom" />

      <div className="relative z-10 max-w-4xl mx-auto text-center pt-24 pb-24 w-full">

        {/* Banner Nueva Propuesta - DESTACADO ARRIBA DEL TÍTULO */}
        <a href="/propuesta" className="block mb-10 max-w-2xl mx-auto" data-testid="hero-banner-nueva-propuesta">
          <div className="bg-gradient-to-r from-orange-500/90 to-orange-600/90 backdrop-blur-sm rounded-2xl p-5 shadow-2xl border-2 border-orange-400/30 hover:scale-[1.02] transition-all duration-300 hover:shadow-orange-500/20">
            <div className="text-center">
              <div className="text-white font-bold text-lg mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Nueva Propuesta Disponible
              </div>
              <div className="text-orange-100 text-xs mb-3">
                Sistema de Tasación Integrado · Demo interactiva lista para explorar
              </div>
              <span className="inline-block bg-white text-orange-600 px-5 py-2 rounded-lg font-bold hover:bg-orange-50 transition-all shadow-md text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ver Propuesta →
              </span>
            </div>
          </div>
        </a>

        <img src={LOGO_CIRCLE} alt="Artificialmente" className="h-12 w-12 rounded-full ring-2 ring-white/10 mx-auto mb-8 opacity-90" data-testid="hero-logo" />

        <div className="inline-flex items-center gap-3 mb-7" data-testid="hero-overline">
          <span className="h-px w-8 bg-white/20" />
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400" style={{ fontFamily: 'Outfit, sans-serif' }}>Propuesta comercial · Vladimir & Aerocar · 2025</span>
          <span className="h-px w-8 bg-white/20" />
        </div>

        <h1 data-testid="hero-title"
          className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight tracking-tight mb-6 text-white"
          style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}>
          Propuesta de reestructura<br />
          <span className="font-bold" style={{ color: '#EA580C' }}>de gestión digital.</span>
        </h1>

        <p data-testid="hero-subtitle" className="text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed" style={{ fontSize: '16px' }}>
          Esta propuesta presenta el desarrollo de una plataforma unificada para Automotora Vladimir y Aerocar — integrada por seis sistemas conectados entre sí, diseñada para centralizar procesos, optimizar la gestión operativa y potenciar la presencia digital del grupo.
        </p>

        <div className="mt-16 flex flex-col items-center" data-testid="scroll-indicator">
          <div className="scroll-indicator" onClick={() => go('ecosistema')}>
            <div className="scroll-hint-label">Explorar propuesta</div>
            <div className="mouse-icon">
              <div className="mouse-wheel" />
            </div>
            <ChevronDown size={16} className="scroll-chevron-1" />
            <ChevronDown size={16} className="scroll-chevron-2" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Ecosistema ──────────────────────────────────────── */
const MODS_OV = [
  { n: '01', icon: <Globe size={18} />,        title: 'Web + Publicador Automático', body: 'Sitio institucional con panel de administración propio, catálogo dinámico de vehículos y API de integración interna. Publicación multicanal a MercadoLibre, Facebook e Instagram desde un único panel.' },
  { n: '02', icon: <MessageSquare size={18} />, title: 'CRM Multicanal',              body: 'Bandeja unificada para WhatsApp, Facebook e Instagram. Asignación de leads por vendedor, historial completo por cliente y seguimiento del proceso de venta desde el primer contacto.' },
  { n: '03', icon: <Calculator size={18} />,   title: 'Tasador de Vehículos',        body: 'Valuación en tiempo real con datos de Autodata.com.uy y MercadoLibre. Notifica a vendedores por WhatsApp y consolida todas las estimaciones — incluyendo revendedores — en un promedio.' },
  { n: '04', icon: <Car size={18} />,          title: 'Plataforma de Usados',        body: 'Plataforma B2B completa para la comercialización de usados con revendedores. Publicaciones con ciclo de vida, sistema de ofertas, mensajería, cierres y reportes de operación.' },
  { n: '05', icon: <Bot size={18} />,          title: 'Agente de IA 24/7',           body: 'Asistente conversacional entrenado con la información y disponibilidad de vehículos del grupo. Disponible fuera del horario comercial, se integra al CRM y escala al equipo cuando corresponde.' },
  { n: '06', icon: <Settings size={18} />,     title: 'Sistema Integrador',          body: 'Hub central que conecta todos los módulos contratados bajo un panel unificado. Arquitectura abierta para incorporar nuevos sistemas en el futuro sin reconstruir desde cero.' },
];

function EcosistemaSection() {
  return (
    <section id="ecosistema" className="bg-b py-24 sm:py-32 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="fade-up mb-12">
          <div className="sec-label">
            <span className="sec-num">01</span><span className="sec-line" /><span className="sec-name">La Propuesta</span>
          </div>
          <h2 className="sec-h2 mb-4">Pack de cinco sistemas integrados<br /><span className="accent">en una sola plataforma.</span></h2>
          <p className="max-w-xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '17px', color: '#4B5563', fontWeight: 400, lineHeight: 1.6 }}>Con opción de contratarlos individualmente.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {MODS_OV.map((m, i) => (
            <div key={i} data-testid={`ov-mod-${i}`} className={`fade-up d${(i % 3) + 1} card-mod`}>
              <div className="card-mod-header">
                <div className="m-icon mb-0">{m.icon}</div>
                <span className="text-accent font-bold" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '11px', letterSpacing: '0.05em' }}>{m.n}</span>
              </div>
              <h3 className="mod-title mb-2">{m.title}</h3>
              <p className="sec-body" style={{ fontSize: '12.5px' }}>{m.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Separador de sección ────────────────────────────── */
function Sep({ n, title }) {
  return (
    <div className="bg-a border-t border-b border-gray-200 px-6 sm:px-10 py-6">
      <div className="max-w-6xl mx-auto flex items-center gap-5">
        <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 300, color: '#DBEAFE', flexShrink: 0 }}>{n}</span>
        <div className="thin-rule flex-1" />
        <span className="text-xs font-bold uppercase tracking-widest text-gray-400 flex-shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</span>
        <div className="thin-rule w-8 flex-shrink-0" />
      </div>
    </div>
  );
}

/* ─── Módulo 01: Web + Publicador Automático (split A/B) ─ */
function ModuloWebPublicador() {
  const webFeats = [
    'Diseño moderno, responsive y optimizado para celular',
    'Catálogo dinámico de vehículos con fichas completas por modelo',
    'Panel de administración propio para el equipo',
    'API interna de integración con el Publicador Automático',
    'Formularios de contacto que ingresan al CRM directamente',
    'Sección de financiación con calculadora de cuotas',
  ];
  const pubFeats = [
    { t: 'Carga única del vehículo', d: 'El equipo sube los datos, fotos y precio una sola vez en el sistema. Desde ahí se gestiona todo sin entrar a cada plataforma.' },
    { t: 'Publicación con un clic', d: 'Se elige en qué plataformas publicar y se envía el contenido. Sin acceder a MercadoLibre, Facebook e Instagram individualmente.' },
  ];
  return (
    <section className="bg-a py-20 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">

        {/* Module header */}
        <div className="fade-up mb-10 flex items-start gap-5">
          <div className="m-icon mt-1"><Globe size={18} /></div>
          <div>
            <div className="sec-label mb-2"><span className="sec-num">01</span><span className="sec-line" /><span className="sec-name">Módulo</span></div>
            <h2 className="sec-h3">Sitio Web <span className="accent">+ Publicador Automático</span></h2>
          </div>
        </div>

        {/* Split A + B */}
        <div className="grid lg:grid-cols-2 gap-6">

          {/* Part A: Sitio Web */}
          <div className="fade-up card p-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="split-badge">A</span>
              <h3 className="text-sm-sub">Sitio Web Institucional</h3>
            </div>
            <p className="sec-body mb-6">El sitio web es la base técnica del ecosistema. No es una web institucional estática: incluye un panel de administración propio desde donde el equipo carga y gestiona vehículos, y una API interna que alimenta al Publicador Automático. Todo lo que se carga en el sitio se publica en simultáneo en los canales externos.</p>
            {/* VW logo display */}
            <div className="flex items-center gap-4 mb-6 p-4 rounded-xl border border-gray-100 bg-gray-50">
              <div className="brand-logo-area-sm" style={{ background: '#F5F5F5', borderRadius: 8, padding: 10, width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <img src="/logos/volkswagen.svg" alt="Volkswagen" style={{ maxHeight: 44, maxWidth: 44, objectFit: 'contain' }} onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} />
                <span style={{ display:'none', fontFamily:'Outfit,sans-serif', fontSize:12, fontWeight:800, color:'#1B2A4A' }}>VW</span>
              </div>
              <div>
                <div className="text-sm-sub" style={{ fontSize: 13 }}>Representante oficial Volkswagen</div>
                <div className="sec-body" style={{ fontSize: 12 }}>Sitio diseñado con identidad de marca Volkswagen, catálogo integrado y presencia digital consistente.</div>
              </div>
            </div>
            <ul className="space-y-2.5">
              {webFeats.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="feat-dot" style={{ marginTop: 8 }} />
                  <span className="sec-body text-sm">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Part B: Publicador Automático */}
          <div className="fade-up d2 card p-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="split-badge split-badge-b">B</span>
              <h3 className="text-sm-sub">Publicador Automático</h3>
            </div>
            <p className="sec-body mb-6">Con el sitio como base, el publicador multicanal permite que cada vehículo cargado en el sistema se distribuya en el sitio web y en los portales externos. Elimina el trabajo manual de entrar a cada plataforma por separado.</p>

            <div className="space-y-3 mb-5">
              <div className="card-flat p-4 flex items-center gap-4">
                <div className="m-icon-sm mb-0"><Package size={14} /></div>
                <div>
                  <div className="text-sm-sub" style={{ fontSize: 13 }}>Alta del vehículo en Sistema Central</div>
                  <div className="text-xs text-gray-400 mt-0.5">Datos, fotos, precio y estado de disponibilidad</div>
                </div>
              </div>
              <div className="flex justify-center"><div className="w-px h-4 bg-gray-200" /></div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { n: 'Sitio Web', c: '#1D4ED8', i: <Globe size={11} /> },
                  { n: 'MercadoLibre', c: '#EAB308', i: <ShoppingBag size={11} /> },
                  { n: 'Facebook', c: '#3B82F6', i: <Share2 size={11} /> },
                  { n: 'Instagram', c: '#EC4899', i: <MessageCircle size={11} /> }
                ].map((pl, i) => (
                  <div key={i} className="card p-2.5 text-center">
                    <div className="flex justify-center mb-1" style={{ color: pl.c }}>{pl.i}</div>
                    <div className="text-gray-500" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 9, fontWeight: 700 }}>{pl.n}</div>
                    <div className="text-gray-400 mt-0.5" style={{ fontSize: 9 }}>Auto.</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {pubFeats.map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0 bg-blue-50 border border-blue-200">
                    <Check size={11} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm-sub mb-0.5" style={{ fontSize: 13 }}>{f.t}</div>
                    <div className="sec-body" style={{ fontSize: '12px' }}>{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Módulo 02: CRM ──────────────────────────────────── */
function ModuloCRM() {
  const inbox = [
    { ch: 'WhatsApp', cc: '#16A34A', icon: <Smartphone size={11} />, name: 'Juan García', msg: 'Hola, ¿me puede dar info sobre el VW Amarok?', time: '10:32', unread: 2 },
    { ch: 'Facebook', cc: '#2563EB', icon: <Share2 size={11} />, name: 'María López', msg: '¿El VW Taos sigue disponible?', time: '09:15', unread: 1 },
    { ch: 'Instagram', cc: '#DB2777', icon: <MessageCircle size={11} />, name: 'Carlos Pérez', msg: 'Vi el Golf GTI en el post de hoy...', time: '08:45', unread: 0 },
  ];
  const feats = [
    { icon: <Users size={14} />, t: 'Asignación de leads a vendedores', d: 'Distribución automática o manual según disponibilidad del equipo de ventas.' },
    { icon: <FileText size={14} />, t: 'Ficha completa por cliente', d: 'Historial de conversaciones, vehículos consultados y estado del proceso de venta.' },
    { icon: <TrendingUp size={14} />, t: 'Pipeline de ventas visible', d: 'Estado de cada lead: nuevo, en seguimiento, cotizado, cerrado ganado o perdido.' },
    { icon: <Zap size={14} />, t: 'Respuestas rápidas predefinidas', d: 'Plantillas para las consultas más frecuentes. El equipo responde en segundos.', opt: true },
  ];
  return (
    <section className="bg-b py-20 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="fade-up mb-10 flex items-start gap-5">
          <div className="m-icon mt-1"><MessageSquare size={18} /></div>
          <div>
            <div className="sec-label mb-2"><span className="sec-num">02</span><span className="sec-line" /><span className="sec-name">Módulo</span></div>
            <h2 className="sec-h3">CRM Multicanal <span className="accent">— Bandeja Unificada</span></h2>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="fade-up">
            <p className="sec-body mb-6">Todas las consultas que llegan por WhatsApp, Facebook e Instagram se centralizan en una única bandeja. Cada mensaje queda registrado, asignado a un vendedor y trazable desde el primer contacto hasta el cierre.</p>
            <div className="card overflow-hidden">
              <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100">
                <div className="text-sm-sub">Bandeja unificada</div>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-xs text-gray-400">3 activas</span></div>
              </div>
              <div>
                {inbox.map((m, i) => (
                  <div key={i} className={`crm-row ${i < inbox.length - 1 ? 'border-b border-gray-50' : ''}`}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${m.cc}15`, border: `1px solid ${m.cc}30`, color: m.cc }}>{m.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{m.name}</span>
                        <span className="text-xs text-gray-400">{m.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded font-bold" style={{ background: `${m.cc}12`, color: m.cc, fontFamily: 'Outfit, sans-serif', fontSize: '9px', letterSpacing: '0.04em' }}>{m.ch}</span>
                        <p className="text-xs text-gray-400 truncate">{m.msg}</p>
                      </div>
                    </div>
                    {m.unread > 0 && <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs text-white font-bold flex-shrink-0 bg-blue-600" style={{ fontSize: '10px' }}>{m.unread}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="fade-up d2 space-y-5 mt-4 lg:mt-0">
            {feats.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="m-icon-sm mt-0.5">{f.icon}</div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-sm-sub">{f.t}</div>
                    {f.opt && <span className="modulo-opcional-badge" style={{ fontSize: 8 }}>Opcional</span>}
                  </div>
                  <div className="sec-body" style={{ fontSize: '12.5px' }}>{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Módulo 03: Tasador ──────────────────────────────── */
function ModuloTasador() {
  const feats = [
    { icon: <Camera size={14} />,    t: 'Ingreso manual o por foto de libreta', d: 'Se puede ingresar marca, modelo, versión, año, kilómetros y fotos manualmente — o capturar los datos directamente desde una foto de la libreta del vehículo.' },
    { icon: <Database size={14} />,  t: 'Consulta automática de fuentes de mercado', d: 'El sistema consulta Autodata.com.uy vía API y MercadoLibre a través de scraping propio para obtener referencias de precio en tiempo real.' },
    { icon: <Phone size={14} />,     t: 'Notificación a vendedores por WhatsApp', d: 'Al enviar la tasación, cada vendedor recibe los datos del vehículo por WhatsApp. Con un checkbox se puede incluir también a los revendedores registrados.' },
    { icon: <Calculator size={14} />, t: 'Consolidación de estimaciones', d: 'Los vendedores responden con su estimación de precio. El sistema muestra todas las valuaciones recibidas —API, scraping y vendedores— y calcula un promedio.' },
  ];
  return (
    <section className="bg-a py-20 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="fade-up mb-10 flex items-start gap-5">
          <div className="m-icon mt-1"><Calculator size={18} /></div>
          <div>
            <div className="sec-label mb-2"><span className="sec-num">03</span><span className="sec-line" /><span className="sec-name">Módulo</span></div>
            <h2 className="sec-h3">Tasador de Vehículos <span className="accent">— Valuación Inteligente</span></h2>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="fade-up space-y-4">
            <p className="sec-body">Este sistema centraliza todas las fuentes en un solo flujo: datos del vehículo, referencias de mercado y estimaciones del equipo de ventas — con la opción de incluir también la opinión de revendedores de confianza — consolidadas en un único resultado.</p>
            {feats.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="m-icon-sm mt-0.5">{f.icon}</div>
                <div>
                  <div className="text-sm-sub mb-1">{f.t}</div>
                  <div className="sec-body" style={{ fontSize: '12.5px' }}>{f.d}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="fade-up d2 space-y-4">
            {/* Input Card */}
            <div className="card overflow-hidden">
              <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100 bg-gray-50">
                <div className="text-sm-sub" style={{ fontSize: 13 }}>Nueva Tasación</div>
                <div className="flex gap-1.5">
                  <button className="tasador-tab active">Carga manual</button>
                  <button className="tasador-tab"><Camera size={9} style={{ display:'inline', marginRight:3 }} />Foto de libreta</button>
                </div>
              </div>
              <div className="p-4 space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <div className="tasador-field"><div className="tasador-label">Marca</div><div className="tasador-val">Volkswagen</div></div>
                  <div className="tasador-field"><div className="tasador-label">Modelo</div><div className="tasador-val">Polo</div></div>
                  <div className="tasador-field"><div className="tasador-label">Versión</div><div className="tasador-val">1.6 MSI Trendline</div></div>
                  <div className="tasador-field"><div className="tasador-label">Año</div><div className="tasador-val">2021</div></div>
                </div>
                <div className="tasador-field"><div className="tasador-label">Kilómetros</div><div className="tasador-val">62.000 km</div></div>
                <div className="pt-1">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 9 }}>Fuentes de valuación</div>
                  <div className="flex gap-2 flex-wrap">
                    {['Autodata.com.uy', 'MercadoLibre'].map(s => (
                      <span key={s} className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 10, fontWeight: 600 }}>
                        <Check size={8} /> {s}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="pt-1">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 9 }}>Notificar a</div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" defaultChecked className="w-3 h-3 accent-blue-600" /><span className="text-xs text-gray-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Vendedores</span></label>
                    <label className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" className="w-3 h-3 accent-blue-600" /><span className="text-xs text-gray-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Revendedores</span></label>
                  </div>
                </div>
                <button className="w-full btn-p justify-center mt-1" style={{ fontSize: 12, padding: '9px 16px' }}>
                  Enviar tasación
                </button>
              </div>
            </div>

            {/* Results Card */}
            <div className="card overflow-hidden">
              <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100 bg-gray-50">
                <div className="text-sm-sub" style={{ fontSize: 13 }}>Resultado — Tasación #0042</div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-700" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 9, fontWeight: 700 }}>4 respuestas</span>
              </div>
              <div className="p-4">
                {[
                  { s: 'Autodata.com.uy', v: 'USD 11.200', c: '#1D4ED8' },
                  { s: 'MercadoLibre (mediana)', v: 'USD 10.900', c: '#EAB308' },
                  { s: 'Vendedor González', v: 'USD 11.000', c: '#059669' },
                  { s: 'Vendedor Martínez', v: 'USD 10.750', c: '#059669' },
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2.5">
                      <div className="w-1.5 h-4 rounded" style={{ background: r.c, flexShrink: 0 }} />
                      <span className="sec-body" style={{ fontSize: 12 }}>{r.s}</span>
                    </div>
                    <span className="font-bold text-blue-900" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 13 }}>{r.v}</span>
                  </div>
                ))}
                <div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-orange-600" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.1em', fontSize: 9 }}>Promedio consolidado</span>
                  <span className="font-bold text-orange-700" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 16 }}>USD 10.962</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Módulo 04: Usados ───────────────────────────────── */
function ModuloUsados() {
  const feats = [
    { icon: <Phone size={14} />,    t: 'Publicaciones con ciclo de vida completo', d: 'Cada unidad tiene estados: activa, en espera, adjudicada, vencida. El equipo ve en todo momento qué está pasando con cada vehículo.' },
    { icon: <Tag size={14} />,      t: 'Sistema de ofertas entre revendedores',   d: 'Los revendedores registrados reciben las publicaciones y pueden enviar sus ofertas directamente desde el sistema. Se registran todas las propuestas por unidad.' },
    { icon: <BarChart3 size={14} />, t: 'Reportes y trazabilidad',               d: 'Dashboard con métricas de actividad, tasa de adjudicación, revendedores inactivos y estado general de las operaciones.' },
  ];
  return (
    <section className="bg-b py-20 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="fade-up mb-10 flex items-start gap-5">
          <div className="m-icon mt-1"><Car size={18} /></div>
          <div>
            <div className="sec-label mb-2"><span className="sec-num">04</span><span className="sec-line" /><span className="sec-name">Módulo</span></div>
            <h2 className="sec-h3">Sistema de Autos Usados <span className="accent">para Reventa</span></h2>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="fade-up space-y-4">
            <p className="sec-body">Plataforma B2B completa para gestionar la comercialización de usados entre Vladimir y Aerocar y su red de revendedores. Desde la publicación de cada unidad hasta el cierre de la operación, todo queda registrado y trazable en un solo lugar.</p>
            {feats.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="m-icon-sm mt-0.5">{f.icon}</div>
                <div>
                  <div className="text-sm-sub mb-1">{f.t}</div>
                  <div className="sec-body" style={{ fontSize: '12.5px' }}>{f.d}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="fade-up d2">
            <div className="card p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>Sistema en producción — métricas reales</div>
              {[
                { l: 'Publicaciones gestionadas',    v: '315 unidades'  },
                { l: 'Ofertas registradas',           v: '+1.400 ofertas' },
                { l: 'Revendedores activos',          v: '127 contactos' },
                { l: 'Tasa de adjudicación',          v: '8.89%'         },
              ].map((s, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-0.5 h-4 rounded bg-orange-500 flex-shrink-0" />
                    <div className="text-sm text-gray-500">{s.l}</div>
                  </div>
                  <div className="text-sm font-bold text-blue-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.v}</div>
                </div>
              ))}
              <div className="mt-4 p-4 rounded-lg text-xs text-gray-400 leading-relaxed bg-blue-50 border border-blue-100">
                Sistema ya desarrollado y en operación. Se integra al ecosistema de Vladimir y Aerocar con la identidad de marca correspondiente.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Módulo 05: Agente IA (Opcional) ─────────────────── */
function ModuloIA() {
  const feats = [
    { icon: <Clock size={14} />,    t: 'Sin ventana de silencio',             d: 'Un cliente que consulta a las 11pm recibe respuesta inmediata. Cada lead, atendido.' },
    { icon: <Database size={14} />, t: 'Conectado a la información del grupo', d: 'Disponibilidad de vehículos, precios y consultas frecuentes siempre actualizados. Sin respuestas genéricas.' },
    { icon: <Shield size={14} />,   t: 'Escalado a vendedor cuando corresponde', d: 'Cuando la consulta requiere atención humana, transfiere con todo el contexto.' },
  ];
  return (
    <section className="bg-a py-20 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="fade-up mb-10 flex items-start gap-5">
          <div className="m-icon mt-1"><Bot size={18} /></div>
          <div>
            <div className="sec-label mb-2">
              <span className="sec-num">05</span><span className="sec-line" /><span className="sec-name">Módulo</span>
              <span className="modulo-opcional-badge">Opcional</span>
            </div>
            <h2 className="sec-h3">Agente de IA <span className="accent">disponible 24/7</span></h2>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="fade-up">
            <p className="sec-body mb-6">El Agente de IA puede integrarse como un módulo adicional al ecosistema. Entrenado con la información y disponibilidad de vehículos del grupo, atiende consultas fuera del horario comercial —como un vendedor más pero sin horarios ni límites.</p>
            <div className="card overflow-hidden">
              <div className="px-5 py-3.5 flex items-center gap-3 border-b border-gray-100 bg-gray-50">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-50 border border-blue-200"><Bot size={14} className="text-blue-600" /></div>
                <div>
                  <div className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Outfit, sans-serif' }}>Asistente Vladimir & Aerocar</div>
                  <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-green-500" /><div className="text-xs text-gray-400">Activo ahora</div></div>
                </div>
              </div>
              <div className="p-4 space-y-0.5">
                <div className="chat-bot">
                  <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center bg-blue-50 border border-blue-200"><Bot size={10} className="text-blue-600" /></div>
                  <div className="bubble-bot">Hola, soy el asistente de Vladimir & Aerocar. Estoy disponible para consultas, precios e información de vehículos. ¿En qué te ayudo?</div>
                </div>
                <div className="chat-user">
                  <div className="w-6 h-6 rounded-full bg-gray-100 border border-gray-200 flex-shrink-0 flex items-center justify-center"><Users size={10} className="text-gray-400" /></div>
                  <div className="bubble-user">¿Cuánto sale el VW Amarok?</div>
                </div>
                <div className="chat-bot">
                  <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center bg-blue-50 border border-blue-200"><Bot size={10} className="text-blue-600" /></div>
                  <div className="bubble-bot">El VW Amarok está desde <strong className="text-blue-700">U$S 42.000</strong>. Hay disponibilidad en nuestro local de Av. Italia. ¿Agendamos una prueba de manejo?</div>
                </div>
              </div>
            </div>
          </div>
          <div className="fade-up d2 space-y-5 mt-4 lg:mt-0">
            {feats.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="m-icon-sm mt-0.5">{f.icon}</div>
                <div>
                  <div className="text-sm-sub mb-1">{f.t}</div>
                  <div className="sec-body" style={{ fontSize: '12.5px' }}>{f.d}</div>
                </div>
              </div>
            ))}
            <div className="mt-4 p-5 rounded-xl bg-blue-50 border border-blue-100">
              <p className="text-xs text-blue-700 leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600 }}>Este módulo puede contratarse de forma independiente o como parte del paquete integral. Se integra con el CRM y el resto del ecosistema.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Sistema Central (al final — nuclea todo) ─────────── */
function SistemaCentral() {
  const pilares = [
    { icon: <Settings size={16} />,   t: 'Integración nativa de módulos',  d: 'Cada sistema contratado se conecta al núcleo central de forma nativa. No hay integraciones externas ni conexiones frágiles.' },
    { icon: <Database size={16} />,   t: 'Panel de administración único',   d: 'El equipo gestiona todo desde un solo lugar: publicaciones, clientes, revendedores y estado general de la operación.' },
    { icon: <TrendingUp size={16} />, t: 'Extensible a futuro',             d: 'La arquitectura está diseñada para incorporar nuevos módulos con el tiempo, sin necesidad de reconstruir desde cero.' },
  ];
  return (
    <section className="bg-b py-20 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="fade-up mb-10 flex items-start gap-5">
          <div className="m-icon mt-1"><Settings size={18} /></div>
          <div>
            <div className="sec-label mb-2"><span className="sec-num">06</span><span className="sec-line" /><span className="sec-name">Sistema Integrador</span></div>
            <h2 className="sec-h3">El eje que <span className="accent">conecta todo</span></h2>
          </div>
        </div>
        <p className="fade-up sec-body max-w-2xl mb-10">El Sistema Central es el núcleo al que se conectan todos los módulos. Su función es mantener la coherencia de la plataforma: que cada sistema hable con los demás, que la información no se duplique y que el equipo opere desde un único punto de control. Está diseñado para crecer: cada nuevo módulo que se incorpore en el futuro se integra de forma nativa al ecosistema existente.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {pilares.map((m, i) => (
            <div key={i} data-testid={`central-${i}`} className={`fade-up d${i + 1} card p-6`}>
              <div className="m-icon">{m.icon}</div>
              <h3 className="text-sm-sub mb-2">{m.t}</h3>
              <p className="sec-body" style={{ fontSize: '12.5px' }}>{m.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Roadmap Gantt ───────────────────────────────────── */
const GANTT = [
  { n: 'Análisis y Arquitectura',         s: 0,   e: 1.2, c: '#EA580C' },
  { n: 'Diseño UI/UX',                    s: 0.6, e: 2.2, c: '#F97316' },
  { n: 'Sitio Web Institucional',         s: 1,   e: 3.0, c: '#1D4ED8' },
  { n: 'Publicador Automático',           s: 2.5, e: 4.5, c: '#2563EB' },
  { n: 'CRM Multicanal',                  s: 2,   e: 4.5, c: '#7C3AED' },
  { n: 'Tasador de Vehículos',            s: 2.5, e: 4.5, c: '#059669' },
  { n: 'Autos Usados · Reventa',          s: 3,   e: 5,   c: '#D97706' },
  { n: 'Agente IA 24/7',                  s: 3.5, e: 5.5, c: '#0891B2' },
  { n: 'Sistema Central',                 s: 1.5, e: 5.5, c: '#374151' },
  { n: 'QA · Capacitación · Lanzamiento', s: 5,   e: 6,   c: '#DC2626' },
];
const TM = 6;

function Roadmap() {
  return (
    <section id="roadmap" className="bg-a py-24 sm:py-32 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="fade-up mb-12">
          <div className="sec-label">
            <span className="sec-num">02</span><span className="sec-line" /><span className="sec-name">Cronograma</span>
          </div>
          <h2 className="sec-h2 mb-4">Roadmap de <span className="accent">implementación</span></h2>
          <p className="sec-body max-w-xl">6 meses de trabajo estructurado por fases para transformar la operación digital de Vladimir y Aerocar sin interrumpir la actividad del negocio.</p>
        </div>
        <div className="fade-up gantt-box">
          <div className="gantt-hrow">
            <div />
            <div className="gantt-months">{Array.from({ length: 6 }, (_, i) => <div key={i} className="gantt-mlbl">MES {i + 1}</div>)}</div>
          </div>
          <div className="thin-rule mb-2" />
          {GANTT.map((t, i) => (
            <div key={i} data-testid={`gantt-${i}`} className="gantt-trow">
              <div className="gantt-lbl">{t.n}</div>
              <div className="gantt-track">
                <div className="gantt-bar" style={{ left: `${(t.s / TM) * 100}%`, width: `${((t.e - t.s) / TM) * 100}%`, background: t.c }} />
              </div>
            </div>
          ))}
          <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap gap-x-5 gap-y-2">
            {[
              { c: '#EA580C', l: 'Análisis' },
              { c: '#1D4ED8', l: 'Web & UI' },
              { c: '#7C3AED', l: 'CRM' },
              { c: '#059669', l: 'Tasador' },
              { c: '#D97706', l: 'Autos Usados' },
              { c: '#0891B2', l: 'IA' },
              { c: '#374151', l: 'Sistema Central' },
              { c: '#DC2626', l: 'QA & Lanzamiento' },
            ].map((x, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: x.c }} />
                <span className="text-xs text-gray-400">{x.l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Inversión ───────────────────────────────────────── */
const MODULES_PRICING = [
  { id: '01', m: 'Sitio Web Institucional',          p: 1790  },
  { id: '02', m: 'Publicador Automático',             p: 2610  },
  { id: '03', m: 'CRM Multicanal',                    p: 3240  },
  { id: '04', m: 'Tasador de Vehículos',              p: 3260  },
  { id: '05', m: 'Plataforma de Usados para Reventa', p: 2810  },
  { id: '06', m: 'Sistema Integrador',                p: 2360  },
];
const PACK_PRICE = 11130;
const MONTHLY    = 670;

function Inversion() {
  const individualTotal = MODULES_PRICING.reduce((a, b) => a + b.p, 0);
  const savings  = individualTotal - PACK_PRICE;
  const discount = Math.round((savings / individualTotal) * 100);
  const fmt = n => n.toLocaleString('es-UY');

  return (
    <section id="inversion" className="bg-b py-24 sm:py-32 px-6 sm:px-10">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="fade-up mb-10">
          <div className="sec-label">
            <span className="sec-num">03</span><span className="sec-line" /><span className="sec-name">Inversión</span>
          </div>
          <h2 className="sec-h2 mb-4">Propuesta de <span className="accent">inversión</span></h2>
        </div>

        {/* Pricing model */}
        <div className="fade-up mb-8 card p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.12em' }}>Modelo de contratación</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-50 border border-blue-200"><Package size={14} className="text-blue-700" /></div>
              <div>
                <div className="text-sm-sub mb-0.5">Pago único</div>
                <p className="sec-body" style={{ fontSize: '12px' }}>Desarrollo completo, pruebas, setup e implementación.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-orange-50 border border-orange-200"><Clock size={14} className="text-orange-600" /></div>
              <div>
                <div className="text-sm-sub mb-0.5">Mensualidad</div>
                <p className="sec-body" style={{ fontSize: '12px' }}>Servicios asociados (Meta, Google Cloud, OpenAI, etc.), soporte, actualizaciones y mejoras.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison — carousel on mobile, side-by-side on desktop */}
        <div className="fade-up pricing-comparison">

          {/* LEFT — Individual */}
          <div className="pricing-card-indiv">
            <div className="pricing-card-head">
              <span className="pricing-card-label">Contratación individual</span>
            </div>

            {/* Big total price — top for alignment */}
            <div className="pricing-total-row">
              <div className="pricing-total-label">Total si contratás por separado</div>
              <div className="pricing-total-num">USD {fmt(individualTotal)}</div>
              <div className="pricing-sub-note">+ USD {fmt(MONTHLY)}/mes</div>
            </div>

            <div className="pricing-divider" />

            {/* Module list */}
            <div>
              {MODULES_PRICING.map((item, i) => (
                <div key={i} className="pricing-row">
                  <div className="flex items-center gap-2.5">
                    <span className="pricing-num">{item.id}</span>
                    <span className="pricing-mod-name">{item.m}</span>
                  </div>
                  <span className="pricing-mod-price">USD {fmt(item.p)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Pack */}
          <div className="pricing-card-pack">
            <div className="pricing-card-head">
              <span className="pricing-card-label pack-label">Pack completo</span>
              <div className="discount-badge">-{discount}%</div>
            </div>

            {/* Big pack price — top, same height as individual total */}
            <div className="pricing-total-row">
              <div className="pricing-total-label">Precio del pack</div>
              <div className="flex items-start gap-3">
                <div>
                  <div className="pricing-strike">USD {fmt(individualTotal)}</div>
                  <div className="pricing-pack-num">USD {fmt(PACK_PRICE)}</div>
                </div>
              </div>
              <div className="pricing-sub-note" style={{ color: '#EA580C' }}>+ USD {fmt(MONTHLY)}/mes · forma de pago a convenir</div>
            </div>

            <div className="pricing-divider" />

            {/* Savings + info */}
            <div className="savings-box">
              <div className="savings-amount">Ahorrás USD {fmt(savings)}</div>
              <div className="savings-sub">contratando el pack vs módulos individuales</div>
            </div>

            <div className="mt-4 text-xs text-gray-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif', lineHeight: 1.5 }}>
              Incluye los 5 módulos + Sistema Integrador. La mensualidad cubre todos los servicios de terceros, soporte y mejoras continuas.
            </div>

            <a href="mailto:andres@artificialmente.uy" data-testid="contact-cta" className="pricing-cta-btn">
              <Mail size={13} /> Coordinar reunión
            </a>
          </div>

        </div>

        {/* Mobile swipe hint */}
        <div className="flex items-center justify-center gap-1.5 mt-4 md:hidden">
          <div className="w-5 h-1.5 rounded-full bg-blue-700" />
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
          <span className="text-xs text-gray-400 ml-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Deslizá para ver el pack</span>
        </div>

      </div>
    </section>
  );
}


/* ─── Próximos Pasos ──────────────────────────────────── */
function ProximosPasos() {
  const steps = [
    { n: '01', t: 'Reunión de kickoff', d: 'Revisamos la propuesta en detalle, ajustamos el alcance y resolvemos dudas con el equipo directivo de Vladimir y Aerocar.' },
    { n: '02', t: 'Propuesta final y contrato', d: 'Presentamos los valores definitivos, ajustamos el scope si es necesario y firmamos el contrato.' },
    { n: '03', t: 'Inicio del proyecto', d: 'Comienza la fase de análisis e inmersión en el negocio del grupo para arrancar el desarrollo.' },
  ];
  return (
    <section className="bg-a py-24 sm:py-32 px-6 sm:px-10">
      <div className="max-w-4xl mx-auto">
        <div className="fade-up mb-12">
          <div className="sec-label">
            <span className="sec-num">04</span><span className="sec-line" /><span className="sec-name">Próximos pasos</span>
          </div>
          <h2 className="sec-h2">Así arrancamos.</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          {steps.map((s, i) => (
            <div key={i} data-testid={`step-${i}`} className={`fade-up d${i + 1} card p-7`}>
              <div className="flex items-start justify-between mb-5">
                <div className="m-icon mb-0"><Calendar size={16} /></div>
                <span className="font-light text-gray-100" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '32px' }}>{s.n}</span>
              </div>
              <h3 className="text-sm-sub mb-2">{s.t}</h3>
              <p className="sec-body" style={{ fontSize: '13px' }}>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ──────────────────────────────────────────── */
function Footer() {
  return (
    <footer id="footer" className="footer-wrap py-16 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          <div>
            <img src={LOGO_HORIZ_WHITE} alt="Artificialmente" className="h-9 w-auto mb-5 opacity-90" />
            <p className="text-sm text-gray-500 max-w-xs leading-relaxed mb-6">Empresa especializada en inteligencia empresarial y transformación digital. Desarrollamos plataformas a medida para empresas que quieren operar al siguiente nivel.</p>
            
            {/* Destacado: Nueva Propuesta */}
            <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/10 border border-orange-500/20 rounded-xl p-4 max-w-xs">
              <p className="text-sm font-bold text-orange-500 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Nueva Propuesta Disponible
              </p>
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                Sistema de Tasación Integrado - Demo interactiva
              </p>
              <a
                href="/propuesta"
                className="inline-flex items-center gap-2 text-sm font-semibold text-orange-500 hover:text-orange-600 transition"
              >
                Ver propuesta →
              </a>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-10 sm:gap-16">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest mb-4 text-gray-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Contacto</div>
              <div className="space-y-2.5">
                <a href="mailto:andres@artificialmente.uy" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
                  <Mail size={12} /> andres@artificialmente.uy
                </a>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin size={12} /> Uruguay
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest mb-4 text-gray-600" style={{ fontFamily: 'Outfit, sans-serif' }}>Esta propuesta</div>
              <div className="text-sm font-semibold text-gray-300">Vladimir & Aerocar</div>
              <div className="text-xs text-gray-500 mt-1">Montevideo · Av. Italia 5127 esq. Gallinal</div>
              <div className="text-xs text-gray-600 mt-1">Representante oficial Volkswagen</div>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-gray-700">© 2025 Artificialmente · Todos los derechos reservados</div>
          <div className="text-xs text-gray-800">Propuesta confidencial preparada exclusivamente para Vladimir & Aerocar</div>
        </div>
      </div>
    </footer>
  );
}

/* ─── Proposal Component (after unlock) ───────────────── */
function Proposal() {
  useScrollReveal();
  return (
    <div style={{ background: '#FFFFFF', minHeight: '100vh' }}>
      <ScrollProgress />
      <SideNav />
      <Nav />
      <Hero />
      <EcosistemaSection />
      <Sep n="01–06" title="Detalle de módulos" />
      <div id="modulos">
        <ModuloWebPublicador />
        <ModuloCRM />
        <ModuloTasador />
        <ModuloUsados />
        <ModuloIA />
        <SistemaCentral />
      </div>
      <Roadmap />
      <Inversion />
      <ProximosPasos />
      <Footer />
    </div>
  );
}

/* ─── App ─────────────────────────────────────────────── */
function App() {
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem('vladimir_unlocked') === '1');
  if (!unlocked) return <AccessGate onUnlock={() => setUnlocked(true)} />;
  return <Proposal />;
}

export default App;
